// Shared axios client for Spa / Laundry / MiniBar / Housekeeping modules.
// Wraps config.API_BASE_URL + JWT token from sessionStorage.
import axios from 'axios';
import config from '../../config';

const getToken = () => sessionStorage.getItem('token');
const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const api = axios.create({ baseURL: config.API_BASE_URL });

api.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// Convenience helper — current hotel/company from session
export const ctx = () => {
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const userId = parseInt(sessionStorage.getItem('userId') || '0', 10) || null;
  return {
    companyId: company?.id || 0,
    hotelId: company?.hotelId || company?.id || 0,
    userId,
    currency: company?.currency || '₹',
  };
};

// ── Shared lookups ────────────────────────────────────────────────────
export const lookupApi = {
  // Currently in-house guests (checked-in, not cancelled, checkout in future).
  // Returns: [{ bookingId, roomId, roomNumber, guestName, mobileNumber, checkIn, checkOut }]
  inHouseGuests: (hotelId) =>
    api.get('/inhouse-guests', { params: { hotelId } }),
};

// ── Spa ───────────────────────────────────────────────────────────────
export const spaApi = {
  listServices: (hotelId, params) =>
    api.get('/spa/services', { params: { hotelId, ...params } }),
  saveService: (dto, userId) =>
    api.post('/spa/services', dto, { params: { userId } }),
  deleteService: (id, userId) =>
    api.delete(`/spa/services/${id}`, { params: { userId } }),

  listTherapists: (hotelId, params) =>
    api.get('/spa/therapists', { params: { hotelId, ...params } }),
  saveTherapist: (dto, userId) =>
    api.post('/spa/therapists', dto, { params: { userId } }),
  deleteTherapist: (id, userId) =>
    api.delete(`/spa/therapists/${id}`, { params: { userId } }),

  listBookings: (hotelId, params) =>
    api.get('/spa/bookings', { params: { hotelId, ...params } }),
  getBooking: (id) => api.get(`/spa/bookings/${id}`),
  createBooking: (dto) => api.post('/spa/bookings', dto),
  cancelBooking: (id, reason, userId) =>
    api.post(`/spa/bookings/${id}/cancel`, null, { params: { reason, userId } }),
  addPayment: (dto) => api.post('/spa/payments', dto),
  refund: (id, amount, reference, userId) =>
    api.post(`/spa/bookings/${id}/refund`, null, {
      params: { amount, reference, userId },
    }),
  generateInvoice: (id, userId) =>
    api.post(`/spa/bookings/${id}/invoice`, null, { params: { userId } }),
  dashboard: (companyId, hotelId, fromDate, toDate) =>
    api.get('/spa/dashboard', {
      params: { companyId, hotelId, fromDate, toDate },
    }),
};

// ── Laundry ───────────────────────────────────────────────────────────
export const laundryApi = {
  listItems: (hotelId, params) =>
    api.get('/laundry/items', { params: { hotelId, ...params } }),
  saveItem: (dto, userId) =>
    api.post('/laundry/items', dto, { params: { userId } }),
  deleteItem: (id, userId) =>
    api.delete(`/laundry/items/${id}`, { params: { userId } }),

  listOrders: (hotelId, params) =>
    api.get('/laundry/orders', { params: { hotelId, ...params } }),
  getOrder: (id) => api.get(`/laundry/orders/${id}`),
  createOrder: (dto) => api.post('/laundry/orders', dto),
  changeStatus: (dto) => api.post('/laundry/orders/status', dto),
  history: (id) => api.get(`/laundry/orders/${id}/history`),
  reportDaily: (hotelId, date) =>
    api.get('/laundry/reports/daily', { params: { hotelId, date } }),
  reportPending: (hotelId) =>
    api.get('/laundry/reports/pending', { params: { hotelId } }),
  reportRevenue: (hotelId, from, to) =>
    api.get('/laundry/reports/revenue', { params: { hotelId, from, to } }),
};

// ── Mini-Bar ──────────────────────────────────────────────────────────
export const miniBarApi = {
  listItems: (hotelId, params) =>
    api.get('/minibar/items', { params: { hotelId, ...params } }),
  saveItem: (dto, userId) =>
    api.post('/minibar/items', dto, { params: { userId } }),
  deleteItem: (id, userId) =>
    api.delete(`/minibar/items/${id}`, { params: { userId } }),
  lowStock: (hotelId) =>
    api.get('/minibar/items/low-stock', { params: { hotelId } }),

  listAssignments: (hotelId, roomId) =>
    api.get('/minibar/assignments', { params: { hotelId, roomId } }),
  saveAssignment: (dto, userId) =>
    api.post('/minibar/assignments', dto, { params: { userId } }),

  recordConsumption: (dto, userId) =>
    api.post('/minibar/consumption', dto, { params: { userId } }),
  listConsumptions: (hotelId, params) =>
    api.get('/minibar/consumption', { params: { hotelId, ...params } }),
  replenish: (dto) => api.post('/minibar/replenish', dto),
  ledger: (hotelId, params) =>
    api.get('/minibar/ledger', { params: { hotelId, ...params } }),
  reportRoomWise: (hotelId, from, to) =>
    api.get('/minibar/reports/room-wise', { params: { hotelId, from, to } }),
  reportItemWise: (hotelId, from, to) =>
    api.get('/minibar/reports/item-wise', { params: { hotelId, from, to } }),
};

// ── Housekeeping ──────────────────────────────────────────────────────
export const housekeepingApi = {
  listStaff: (hotelId, params) =>
    api.get('/housekeeping/staff', { params: { hotelId, ...params } }),
  saveStaff: (dto, userId) =>
    api.post('/housekeeping/staff', dto, { params: { userId } }),
  deleteStaff: (id, userId) =>
    api.delete(`/housekeeping/staff/${id}`, { params: { userId } }),

  listTasks: (hotelId, params) =>
    api.get('/housekeeping/tasks', { params: { hotelId, ...params } }),
  createTask: (dto) => api.post('/housekeeping/tasks', dto),
  changeTaskStatus: (dto) => api.post('/housekeeping/tasks/status', dto),

  roomStatus: (hotelId) =>
    api.get('/housekeeping/rooms/status', { params: { hotelId } }),
  setRoomStatus: (hotelId, roomId, status, notes, userId) =>
    api.post(`/housekeeping/rooms/${roomId}/status`, null, {
      params: { hotelId, status, notes, userId },
    }),

  listMaintenance: (hotelId, status) =>
    api.get('/housekeeping/maintenance', { params: { hotelId, status } }),
  raiseMaintenance: (dto, userId) =>
    api.post('/housekeeping/maintenance', dto, { params: { userId } }),
  updateMaintenance: (dto, userId) =>
    api.put('/housekeeping/maintenance', dto, { params: { userId } }),

  dashboard: (companyId, hotelId) =>
    api.get('/housekeeping/dashboard', { params: { companyId, hotelId } }),
};

export default api;
